class CategoryAPILayer {
  constructor () {
    this.eventBus = window.theme.category.eventBus

    this.apiKey = window.theme.collection.klevuAPIKey
    this.endpoint = window.theme.collection.klevuAPIURL
    this.market = `${window.Shopify.currency.active}_${window.Shopify.country}`

    this.initialLoad = true

    // Cache for storing complete product sets per collection/filter/sort combination
    this.productCache = new Map()
    this.backgroundFetchInProgress = false

    // Bundle anchors loading state
    this.bundleAnchorsReady = !window.theme.collection?.bundle
      ?.anchorCardsEnabled
    this.pendingFetchParams = null

    this.offset = this.initalizeCurrentPageFromSearchParmas()
    this.activeSort = this.initializeActiveSortFromSearchParams()
    this.activeFacets = this.initializeActiveFacetsFromSearchParams()

    this.setupBundleAnchorsListener()

    // Fetch immediately if bundle anchors are ready or not needed
    // TODO:  This needs to be removed when we have improved the collection
    //        loading performance, and to re-implement this PR: https://github.com/baoagency/abask/pull/1450
    //        because we need to wait for the anchors to be loaded on every PLP.
    if (this.bundleAnchorsReady) {
      this.fetchProducts(this.offset, this.activeSort, this.activeFacets)
    } else {
      // Store params to fetch once anchors are loaded
      this.pendingFetchParams = {
        offset: this.offset,
        sort: this.activeSort,
        facets: this.activeFacets,
      }
    }

    this.subscribeToEvents()
    this.getUUID()
  }

  setupBundleAnchorsListener () {
    // Listen for bundle anchors to be loaded using BAO event system
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.BUNDLE.ANCHORS_LOADED,
      anchors => {
        this.bundleAnchorsReady = true

        // If we have pending fetch params, execute the fetch now
        if (this.pendingFetchParams) {
          const { offset, sort, facets } = this.pendingFetchParams
          this.fetchProducts(offset, sort, facets)
          this.pendingFetchParams = null
        }
      },
    )
  }

  subscribeToEvents () {
    this.eventBus.subscribe('state:offset:updated', offset => {
      this.offset = offset
      if (this.bundleAnchorsReady) {
        this.fetchProducts(offset, this.activeSort, this.activeFacets)
      } else {
        this.pendingFetchParams = {
          offset,
          sort: this.activeSort,
          facets: this.activeFacets,
        }
      }
    })

    this.eventBus.subscribe('state:sort:updated', sort => {
      this.offset = window.theme.category.stateLayer.state.pagination.offset
      if (this.bundleAnchorsReady) {
        this.fetchProducts(this.offset, sort, this.activeFacets)
      } else {
        this.pendingFetchParams = {
          offset: this.offset,
          sort,
          facets: this.activeFacets,
        }
      }
    })

    this.eventBus.subscribe('state:activeFacets:updated', () => {
      this.offset = window.theme.category.stateLayer.state.pagination.offset
      if (this.bundleAnchorsReady) {
        this.fetchProducts(this.offset, this.activeSort, this.activeFacets)
      } else {
        this.pendingFetchParams = {
          offset: this.offset,
          sort: this.activeSort,
          facets: this.activeFacets,
        }
      }
    })
  }

  /**
   * Generate cache key for current collection/filter/sort combination
   */
  getCacheKey () {
    const filtersString = this.activeFacets
      .map(facet => `${facet.key}:${[...facet.values].sort().join(',')}`)
      .sort()
      .join('|')
    return `${this.collectionTitle}-${this.activeSort}-${filtersString}`
  }

  /**
   * Check if we can serve request from cache
   */
  canServeFromCache (offset) {
    const cacheKey = this.getCacheKey()
    const cached = this.productCache.get(cacheKey)

    if (!cached || !cached.complete) {
      return false
    }

    // Check if requested page exists in cache
    const startIndex = offset
    const endIndex = offset + this.limit
    const canServe =
      cached.allProducts.length >= endIndex ||
      startIndex < cached.allProducts.length

    return canServe
  }

  /**
   * Serve paginated results from cache
   */
  serveFromCache (offset) {
    const cacheKey = this.getCacheKey()
    const cached = this.productCache.get(cacheKey)

    const startIndex = offset
    const endIndex = offset + this.limit
    const pageProducts = cached.allProducts.slice(startIndex, endIndex)

    const mockResponse = {
      queryResults: [
        {
          records: pageProducts,
          meta: { totalResultsFound: cached.totalResultsFound },
          filters: cached.filters,
        },
      ],
      isPreFiltered: true, // Flag to indicate these products are already bundle-managed
    }

    this.eventBus.publish('api:products:fetched', mockResponse)
    this.eventBus.publish('api:facets:fetched', cached.filters)
  }

  /**
   * Fetch complete dataset, apply bundle management, and serve the requested page
   */
  async fetchCompleteDatasetAndServePage (requestedOffset) {
    if (this.backgroundFetchInProgress) {
      return
    }

    this.backgroundFetchInProgress = true

    try {
      // 1. Fetch first page to get total count
      const firstPageData = await this.fetchSinglePage(0, this.limit)
      const totalResults = firstPageData.queryResults[0].meta.totalResultsFound
      const firstPageProducts = firstPageData.queryResults[0].records

      let allProducts = [...firstPageProducts]

      // 2. Fetch remaining pages if needed
      const remainingProducts = totalResults - firstPageProducts.length
      if (remainingProducts > 0) {
        const backgroundChunkSize = 1000
        const chunksNeeded = Math.ceil(remainingProducts / backgroundChunkSize)
        const fetchPromises = []

        for (let chunk = 0; chunk < chunksNeeded; chunk++) {
          const chunkOffset = this.limit + chunk * backgroundChunkSize
          fetchPromises.push(
            this.fetchSinglePage(chunkOffset, backgroundChunkSize),
          )
        }

        const remainingChunks = await Promise.all(fetchPromises)
        remainingChunks.forEach(chunkData => {
          allProducts = [...allProducts, ...chunkData.queryResults[0].records]
        })
      }

      // 3. Apply bundle management to complete set
      const filteredProducts = window.theme.category.stateLayer.applyBundleCardManagement(
        allProducts,
      )

      // 4. Cache the filtered result
      const cacheKey = this.getCacheKey()

      const filters = firstPageData.queryResults[0].filters.filter(
        filter => filter.key !== 'name' && filter.key !== 'bundleName',
      )

      this.productCache.set(cacheKey, {
        allProducts: filteredProducts,
        originalTotal: totalResults,
        totalResultsFound: filteredProducts.length,
        filters,
        complete: true,
        timestamp: Date.now(),
      })

      // 5. Serve the requested page
      this.serveFromCache(requestedOffset)
    } catch (error) {
      console.error('❌ Failed to fetch complete dataset:', error)
    } finally {
      this.backgroundFetchInProgress = false
    }
  }

  /**
   * Fetch a single page (used by background fetch)
   */
  async fetchSinglePage (offset, chunkSize = null) {
    const url = `https://${this.endpoint}/cs/v2/search`
    const productFilters = [...this.activeFacets]
    const requestLimit = chunkSize || this.limit

    const options = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recordQueries: [
          {
            filters: {
              filtersToReturn: {
                enabled: true,
                options: {
                  order: 'INDEX',
                  limit: 750,
                },
                rangeFilterSettings: [
                  {
                    key: 'klevu_price',
                    minMax: 'true',
                  },
                ],
              },
              applyFilters: {
                filters: productFilters,
              },
            },
            id: 'categoryProducts',
            typeOfRequest: 'CATNAV',
            settings: {
              sort: this.activeSort || 'RELEVANCE',
              limit: requestLimit,
              offset,
              query: {
                categoryPath: this.collectionTitle,
              },
              fields: [
                'url',
                'image',
                'name',
                'price',
                'id',
                'currency',
                'bundleName',
              ],
              searchPrefs: this.searchPrefs,
            },
          },
        ],
        context: {
          apiKeys: [this.apiKey],
        },
      }),
    }

    try {
      const response = await fetch(url, options)
      const data = await response.json()
      return data
    } catch (error) {
      console.error(`❌ fetchSinglePage failed: offset=${offset}`, error)
      throw error
    }
  }

  async getUUID () {
    const url = `https://stats.ksearchnet.com/analytics/uuid`

    const options = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
    }

    const response = await fetch(url, options)

    const data = await response.json()

    sessionStorage.setItem('klevu_uuid', data.klevu_uuid)
    sessionStorage.setItem('klevu_ip_address', data.ip_address)
  }

  async categoryPageViewed () {
    const uuid = sessionStorage.getItem('klevu_uuid')
    const ipAddress = sessionStorage.getItem('klevu_ip_address')

    const url = `https://stats.ksearchnet.com/analytics/categoryProductViewTracking?klevu_apiKey=${this.apiKey}&klevu_categoryName=${this.collectionTitle}&klevu_categoryPath=${this.collectionTitle}&klevu_productIds=${this.productIds}&klevu_pageStartsFrom=${this.offset}&klevu_uuid=${uuid}&klevu_shopperIp=${ipAddress}`

    await fetch(url)
  }

  async productViewed (productId, variantId) {
    const uuid = sessionStorage.getItem('klevu_uuid')
    const ipAddress = sessionStorage.getItem('klevu_ip_address')

    const url = `https://stats.ksearchnet.com/analytics/categoryProductClickTracking?klevu_apiKey=${this.apiKey}&klevu_categoryName=${this.collectionTitle}&klevu_categoryPath=${this.collectionTitle}&klevu_productId=${variantId}&klevu_productGroupId=${productId}&klevu_productVariantId=${variantId}&klevu_uuid=${uuid}&klevu_shopperIp=${ipAddress}`

    await fetch(url)
  }

  initalizeCurrentPageFromSearchParmas () {
    const url = new URL(window.location.href)
    const page = url.searchParams.get('page')

    if (page) {
      this.currentPage = parseInt(page)
    } else {
      this.currentPage = 1
    }

    return (this.currentPage - 1) * this.limit
  }

  initializeActiveSortFromSearchParams () {
    const url = new URL(window.location.href)
    const sort = url.searchParams.get('sort_by')

    let activeSort = 'RELEVANCE'

    if (sort) {
      activeSort = sort
    }

    return activeSort
  }

  initializeActiveFacetsFromSearchParams () {
    const url = new URL(window.location.href)
    const filtersParam = url.searchParams.get('filters')

    const activeFacets = []

    if (filtersParam) {
      const filterGroups = filtersParam.split('&f-')
      filterGroups[0] = filterGroups[0].replace(/^f-/, '')

      if (
        filterGroups.length > 0 &&
        filterGroups[filterGroups.length - 1].endsWith('&')
      ) {
        filterGroups[filterGroups.length - 1] = filterGroups[
          filterGroups.length - 1
        ].replace(/&$/, '')
      }

      filterGroups.forEach(filterPair => {
        const [facetKey, facetValues] = filterPair.split('=')

        const key = facetKey

        const values = facetValues
          .split(',')
          .map(value => decodeURIComponent(value))

        const existingFacetIndex = activeFacets.findIndex(
          facet => facet.key === facetKey,
        )

        if (existingFacetIndex !== -1) {
          activeFacets[existingFacetIndex].values.push(...values)
        } else {
          activeFacets.push({ key, values, singleSelect: false })
        }
      })
    }

    return activeFacets
  }

  async fetchProducts (offset, activeSort, activeFacets) {
    // Store current search parameters for cache lookup
    this.activeSort = activeSort
    this.activeFacets = activeFacets

    // Check if this is an anchor collection that needs complete dataset
    const anchors = window.theme.collection?.bundle?.anchors || []
    const needsCompleteDataset = anchors.length > 0

    // Try to serve from cache first
    const canServe = this.canServeFromCache(offset)

    if (canServe) {
      this.serveFromCache(offset)
      return
    }

    // For anchor collections, always fetch complete dataset and serve correct page
    if (needsCompleteDataset) {
      await this.fetchCompleteDatasetAndServePage(offset)
      return
    }

    // Cache miss or incomplete - fetch from API
    const url = `https://${this.endpoint}/cs/v2/search`
    const productFilters = [...activeFacets]

    const options = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        recordQueries: [
          {
            filters: {
              filtersToReturn: {
                enabled: true,
                options: {
                  order: 'INDEX',
                  limit: 750,
                },
                rangeFilterSettings: [
                  {
                    key: 'klevu_price',
                    minMax: 'true',
                  },
                ],
              },
              applyFilters: {
                filters: productFilters,
              },
            },
            id: 'categoryProducts',
            typeOfRequest: 'CATNAV',
            settings: {
              sort: activeSort || 'RELEVANCE',
              limit: this.limit,
              offset,
              query: {
                categoryPath: this.collectionTitle,
              },
              fields: [
                'url',
                'image',
                'name',
                'price',
                'id',
                'currency',
                'bundleName',
              ],
              searchPrefs: this.searchPrefs,
            },
          },
        ],
        context: {
          apiKeys: [this.apiKey],
        },
      }),
    }

    const response = await fetch(url, options)
    const data = await response.json()
    const filters = data.queryResults[0].filters.filter(
      filter => filter.key !== 'name' && filter.key !== 'bundleName',
    )

    this.eventBus.publish('api:products:fetched', data)
    this.eventBus.publish('api:facets:fetched', filters)

    if (this.initialLoad) {
      this.categoryPageViewed()
    }

    this.initialLoad = false
  }

  get offset () {
    return window.theme.category.stateLayer.state.pagination.offset
  }

  set offset (offset) {
    window.theme.category.stateLayer.state.pagination.offset = offset
  }

  get activeSort () {
    return window.theme.category.stateLayer.state.activeSort
  }

  get activeFacets () {
    return window.theme.category.stateLayer.state.activeFacets
  }

  set activeFacets (activeFacets) {
    window.theme.category.stateLayer.state.activeFacets = activeFacets
  }

  set activeSort (activeSort) {
    window.theme.category.stateLayer.state.activeSort = activeSort
  }

  get currentPage () {
    return window.theme.category.stateLayer.state.pagination.currentPage
  }

  set currentPage (currentPage) {
    window.theme.category.stateLayer.state.pagination.currentPage = currentPage
  }

  get collectionTitle () {
    return window.theme.collection.title
  }

  get limit () {
    return Number(window.theme.collection.limit)
  }

  get productIds () {
    return window.theme.category.stateLayer.state.products.map(
      product => product.id,
    )
  }

  get searchPrefs () {
    // Note on disableGrouping:
    // By default, Klevu will group product variants so we don't see multiple product cards
    // for the same product in the PLP grid.
    // Abask don't use variants, unless it's for bundle anchor products, which use variants
    // to hold the min/max prices.
    // If we're NOT on a collection that contains anchors, we need to disable grouping.
    // If we ARE on a collection that contains anchors, we need to enable grouping so that
    // we don't get 2x product cards for each anchor e.g. one for min and max price variants.

    const searchPrefs = ['showOutOfStockProducts']
    const anchorCardsPresent =
      window.theme.collection?.bundle?.anchors.length > 0

    if (anchorCardsPresent) {
      searchPrefs.splice(searchPrefs.indexOf('disableGrouping'), 1)
    }

    return searchPrefs
  }
}

window.theme.category.apiLayer = new CategoryAPILayer()
